import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAccessToken } from '../utils/auth';
import { previewIcaoListSync, applyIcaoListSync, getSyncHistory } from '../utils/httpApi';
import { isApiSuccess, getFirstApiError } from '../utils/api';
import { formatRelativeTime } from '../utils/dateTime';
import './Admin.css';
import './AdminReferenceData.css';

export const DATASETS = [
  { id: 'aircraft_type', label: 'Aircraft types' },
  { id: 'country_range', label: 'Country hex ranges' },
  { id: 'airline', label: 'Airlines (insert-only)' },
  { id: 'reg_prefix', label: 'Registration prefixes' },
  { id: 'type_alias', label: 'Type aliases' }
];

const SAMPLE_LIMIT = 10;

/** History rows may be snake_case (raw table rows) or camelCase; read both. */
function pick(row, ...names) {
  for (const n of names) {
    if (row && row[n] != null && row[n] !== '') return row[n];
  }
  return undefined;
}

export function normaliseRun(run) {
  return {
    dataset: pick(run, 'dataset'),
    status: pick(run, 'status') || '',
    finishedAt: pick(run, 'finished_at', 'finishedAt', 'started_at', 'startedAt'),
    inserted: Number(pick(run, 'inserted') ?? 0),
    updated: Number(pick(run, 'updated') ?? 0),
    lockedSkipped: Number(pick(run, 'skipped_locked', 'lockedSkipped', 'skippedLocked') ?? 0),
    orphans: Number(pick(run, 'orphans') ?? 0),
    runBy: pick(run, 'run_by_email', 'runByEmail') || '',
    message: pick(run, 'message') || ''
  };
}

function selectionKey(selected) {
  return DATASETS.map(d => d.id).filter(id => selected.has(id)).join(',');
}

function SampleTable({ title, rows, kind }) {
  if (!rows || rows.length === 0) return null;
  const shown = rows.slice(0, SAMPLE_LIMIT);
  let columns;
  let render;
  if (kind === 'update') {
    // {key, before, after}: show the key and the changed columns.
    columns = ['Key', 'Changes'];
    render = (r) => {
      const before = r.before || {};
      const after = r.after || {};
      const changed = Object.keys(after).filter(k => String(before[k] ?? '') !== String(after[k] ?? ''));
      return [
        r.key,
        changed.map(k => `${k}: ${before[k] ?? '—'} → ${after[k] ?? '—'}`).join('; ')
      ];
    };
  } else if (kind === 'orphan') {
    columns = ['Key'];
    render = (r) => [typeof r === 'string' ? r : JSON.stringify(r)];
  } else if (kind === 'skipped') {
    columns = ['Line', 'Reason'];
    render = (r) => [r.line ?? '', r.reason ?? ''];
  } else {
    const keys = Object.keys(shown[0] || {}).filter(k => k !== 'source');
    columns = keys;
    render = (r) => keys.map(k => r[k] ?? '');
  }
  return (
    <div className="admin-sync-sample">
      <div className="admin-sync-sample-title">{title} ({rows.length})</div>
      <div className="admin-table-container admin-sync-sample-table">
        <table className="admin-table">
          <thead><tr>{columns.map(c => <th key={c}>{c}</th>)}</tr></thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={i}>{render(r).map((v, j) => <td key={j} data-label={columns[j]}>{String(v)}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Preview → apply → history for pulling reference tables from
 * github.com/rikgale/ICAOList. Apply is only enabled for the exact dataset
 * selection that was previewed and sends the preview's content hashes so the
 * backend refuses to apply a file that changed in between.
 */
function AdminIcaoListSync() {
  const [selected, setSelected] = useState(() => new Set(DATASETS.map(d => d.id)));
  const [preview, setPreview] = useState(null);
  const [previewKey, setPreviewKey] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [runs, setRuns] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyError, setHistoryError] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);

  const loadHistory = useCallback(async () => {
    setHistoryError(null);
    try {
      const resp = await getSyncHistory({ limit: 20 }, getAccessToken());
      if (isApiSuccess(resp)) {
        setHistory((resp.runs || []).map(normaliseRun));
      } else {
        setHistoryError(getFirstApiError(resp) || 'Failed to load sync history');
      }
    } catch (e) {
      setHistoryError('Failed to load sync history');
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Latest run per dataset (history arrives newest first; fall back to a sort).
  const lastByDataset = useMemo(() => {
    const out = {};
    history.forEach(run => {
      if (!run.dataset) return;
      const prev = out[run.dataset];
      if (!prev || String(run.finishedAt || '') > String(prev.finishedAt || '')) out[run.dataset] = run;
    });
    return out;
  }, [history]);

  const currentKey = selectionKey(selected);
  const previewValid = preview && previewKey === currentKey;

  const toggleDataset = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setRuns(null);
  };

  const datasets = DATASETS.map(d => d.id).filter(id => selected.has(id));

  const runPreview = async () => {
    if (datasets.length === 0) return;
    setPreviewing(true);
    setActionMsg(null);
    setRuns(null);
    try {
      const resp = await previewIcaoListSync({ datasets, sampleSize: SAMPLE_LIMIT }, getAccessToken());
      if (!isApiSuccess(resp)) {
        setPreview(null);
        setPreviewKey(null);
        setActionMsg({ type: 'error', text: getFirstApiError(resp) || 'Preview failed' });
        return;
      }
      setPreview(resp);
      setPreviewKey(selectionKey(selected));
    } catch (e) {
      setPreview(null);
      setPreviewKey(null);
      setActionMsg({ type: 'error', text: 'Preview failed' });
    } finally {
      setPreviewing(false);
    }
  };

  const runApply = async () => {
    if (!previewValid) return;
    const total = (preview.datasets || []).reduce((n, d) => n + (d.toInsert || 0) + (d.toUpdate || 0), 0);
    if (!window.confirm(`Apply ${total.toLocaleString()} inserts/updates across ${datasets.length} dataset(s)? Locked rows are never touched.`)) return;
    setApplying(true);
    setActionMsg(null);
    try {
      const expectedSha256 = {};
      (preview.datasets || []).forEach(d => { if (d.contentSha256) expectedSha256[d.dataset] = d.contentSha256; });
      const resp = await applyIcaoListSync({ datasets, expectedSha256 }, getAccessToken());
      if (!isApiSuccess(resp)) {
        setActionMsg({ type: 'error', text: getFirstApiError(resp) || 'Apply failed' });
        return;
      }
      const results = resp.runs || [];
      setRuns(results);
      const bad = results.filter(r => r.status && r.status !== 'OK');
      setActionMsg({
        type: bad.length ? 'error' : 'success',
        text: bad.length
          ? `${bad.length} dataset(s) did not apply: ${bad.map(r => `${r.dataset} ${r.status}`).join(', ')}`
          : `Applied ${results.length} dataset(s)`
      });
      // The preview no longer reflects the DB; force a fresh one before another apply.
      setPreview(null);
      setPreviewKey(null);
      loadHistory();
    } catch (e) {
      setActionMsg({ type: 'error', text: 'Apply failed' });
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="admin-icaolist-sync">
      <div className="admin-toolbar">
        <div className="admin-sync-datasets" role="group" aria-label="Datasets">
          {DATASETS.map(d => (
            <label key={d.id} className="admin-sync-dataset">
              <input
                type="checkbox"
                checked={selected.has(d.id)}
                onChange={() => toggleDataset(d.id)}
                disabled={previewing || applying}
              />
              {d.label}
            </label>
          ))}
        </div>
        <button type="button" onClick={runPreview} disabled={previewing || applying || datasets.length === 0}>
          {previewing ? 'Previewing…' : 'Preview'}
        </button>
        <button type="button" onClick={runApply} disabled={!previewValid || applying || previewing}>
          {applying ? 'Applying…' : 'Apply'}
        </button>
        <span className="admin-toolbar-meta">
          {preview && !previewValid ? 'Selection changed — preview again before applying' : ''}
        </span>
      </div>

      <div className="admin-info-note">
        Source: <code>github.com/rikgale/ICAOList</code>. Sync inserts new rows and updates unlocked rows;
        it never deletes (orphans are reported only) and never touches rows edited here (locked).
      </div>

      {actionMsg && (
        <div className={`admin-action-message admin-action-${actionMsg.type}`} role="status">
          {actionMsg.text}
        </div>
      )}

      <h3 className="admin-ref-heading">Last sync per dataset</h3>
      {historyError && <div className="admin-action-message admin-action-error">{historyError}</div>}
      <div className="admin-sync-grid">
        {DATASETS.map(d => {
          const last = lastByDataset[d.id];
          return (
            <div key={d.id} className="admin-sync-card admin-sync-card-compact">
              <div className="admin-sync-card-title">{d.label}</div>
              {last ? (
                <div className="admin-sync-card-meta">
                  <span className={`admin-status-badge ${last.status === 'OK' ? 'active' : 'inactive'}`}>{last.status}</span>
                  {' '}{formatRelativeTime(last.finishedAt)} · +{last.inserted} / ~{last.updated}
                  {last.runBy ? ` · ${last.runBy}` : ''}
                </div>
              ) : (
                <div className="admin-sync-card-meta">Never synced</div>
              )}
            </div>
          );
        })}
      </div>

      {runs && (
        <>
          <h3 className="admin-ref-heading">Apply results</h3>
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr><th>Dataset</th><th>Status</th><th>Inserted</th><th>Updated</th><th>Locked skipped</th><th>Orphans</th></tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.dataset}>
                    <td data-label="Dataset">{r.dataset}</td>
                    <td data-label="Status">
                      <span className={`admin-status-badge ${r.status === 'OK' ? 'active' : 'inactive'}`}>{r.status}</span>
                    </td>
                    <td data-label="Inserted" className="num-cell">{r.inserted ?? 0}</td>
                    <td data-label="Updated" className="num-cell">{r.updated ?? 0}</td>
                    <td data-label="Locked skipped" className="num-cell">{r.lockedSkipped ?? 0}</td>
                    <td data-label="Orphans" className="num-cell">{r.orphans ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {preview && (
        <>
          <h3 className="admin-ref-heading">
            Preview{preview.fetchedAt ? ` (fetched ${formatRelativeTime(preview.fetchedAt)})` : ''}
          </h3>
          <div className="admin-sync-grid">
            {(preview.datasets || []).map(d => {
              const label = (DATASETS.find(x => x.id === d.dataset) || {}).label || d.dataset;
              const samples = d.samples || {};
              return (
                <div key={d.dataset} className="admin-sync-card">
                  <div className="admin-sync-card-title">{label}</div>
                  <div className="admin-sync-counts">
                    <div className="admin-sync-count"><span className="admin-sync-count-value">{d.toInsert ?? 0}</span><span className="admin-sync-count-label">Insert</span></div>
                    <div className="admin-sync-count"><span className="admin-sync-count-value">{d.toUpdate ?? 0}</span><span className="admin-sync-count-label">Update</span></div>
                    <div className="admin-sync-count"><span className="admin-sync-count-value">{d.lockedSkipped ?? 0}</span><span className="admin-sync-count-label">Locked</span></div>
                    <div className="admin-sync-count"><span className="admin-sync-count-value">{d.orphans ?? 0}</span><span className="admin-sync-count-label">Orphans</span></div>
                  </div>
                  <div className="admin-sync-card-meta">
                    {d.upstreamRows != null ? `${d.upstreamRows} upstream rows` : ''}
                    {d.parseSkipped ? ` · ${d.parseSkipped} unparseable` : ''}
                    {d.duplicatesDropped ? ` · ${d.duplicatesDropped} duplicates` : ''}
                    {d.unchanged != null ? ` · ${d.unchanged} unchanged` : ''}
                  </div>
                  <SampleTable title="Inserts" rows={samples.insert} kind="insert" />
                  <SampleTable title="Updates" rows={samples.update} kind="update" />
                  <SampleTable title="Orphans" rows={samples.orphan} kind="orphan" />
                  <SampleTable title="Skipped lines" rows={samples.skipped} kind="skipped" />
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default AdminIcaoListSync;
